import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UGCLayout from '@/components/layouts/UGCLayout';
import NavigationBar from '@/components/ui/NavigationBar';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import { storageService } from '@/services/storageService';

const UGCMedia = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-marketplace', userTier);

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ugcNavItems = [
    { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'Layout' },
    { path: '/app/ugc-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  const ugcMoreItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
  ];

  useEffect(() => {
    loadMediaLibrary();
  }, []);

  const loadMediaLibrary = async () => {
    setLoading(true);
    try {
      // Get localStorage video library
      const stored = localStorage.getItem('ugc_video_library');
      if (stored) {
        setVideos(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Failed to load media:', err);
      setError('Could not load your media library');
    } finally {
      setLoading(false);
    }
  };

  const downloadVideo = async (video) => {
    try {
      // Placeholder for future storage download implementation
      alert(`Download for "${video.name}" coming soon - will integrate with Supabase Storage`);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download video');
    }
  };

  const deleteVideo = (videoId) => {
    if (confirm('Delete this video? This action cannot be undone.')) {
      const updated = videos.filter(v => v.id !== videoId);
      setVideos(updated);
      localStorage.setItem('ugc_video_library', JSON.stringify(updated));
    }
  };

  if (!canAccess) {
    return (
      <UGCLayout>
        <NavigationBar items={ugcNavItems} moreItems={ugcMoreItems} hideLocks />
        <div className="min-h-screen flex items-center justify-center bg-muted/30 text-muted-foreground">
          Upgrade your plan to access media storage.
        </div>
      </UGCLayout>
    );
  }

  return (
    <UGCLayout>
      <NavigationBar items={ugcNavItems} moreItems={ugcMoreItems} hideLocks />
      <main className="max-w-6xl mx-auto px-4 py-8 mb-20">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-100 text-xs font-semibold border border-amber-500/30 mb-3">
            <Icon name="Film" size={14} /> Media Library
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Your recorded videos</h1>
          <p className="text-muted-foreground">
            All your UGC recordings, intro videos, and uploaded content. Download, organize, or delete as needed.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
          </div>
        )}

        {!loading && videos.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-12 text-center">
            <Icon name="Film" size={32} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-white mb-2">No videos yet</h3>
            <p className="text-muted-foreground mb-6">
              Your recordings from onboarding and teleprompter sessions will appear here.
            </p>
            <Button onClick={() => navigate('/app/teleprompter')}>
              <Icon name="PlayCircle" size={16} className="mr-2" /> Record a video
            </Button>
          </div>
        )}

        {!loading && videos.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {videos.length} video{videos.length !== 1 ? 's' : ''} • {(videos.reduce((sum, v) => sum + (v.size || 0), 0) / 1024 / 1024).toFixed(2)} MB total
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setVideos([]);
                  localStorage.removeItem('ugc_video_library');
                }}
              >
                Clear all
              </Button>
            </div>

            <div className="grid gap-4">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="rounded-xl border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white truncate mb-1">{video.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {new Date(video.createdAt).toLocaleString()} • {(video.size / 1024).toFixed(0)} KB
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadVideo(video)}
                      >
                        <Icon name="Download" size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => deleteVideo(video.id)}
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </UGCLayout>
  );
};

export default UGCMedia;
